/**
 * NivClinicsTab — clinics overview tab (rendered inside NivDashboardPage).
 *
 * Real clinic data (plan, trial, planEndDate, activity) + the manual CRM
 * layer (paying, payments, notes, tags, contacts) + nightly telemetry
 * usage rows, joined by clinicId in memory.
 *
 * Segments (flat tabs) → toolbar (search / tag chips / plan / sort) →
 * compact status-tinted table rows (payment / next payment / revenue /
 * last contact / last login). Row click opens the detail page — all editing
 * lives there. Tag/contact actions are optimistic (nivApi) and never
 * refetch the heavy overview query.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ContentLoader from "../../../../components/shared/ContentLoader";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import { selectTherapistName } from "../../../../store/slices/authSlice";
import {
  useGetClinicUsageQuery,
  useGetNivClinicsOverviewQuery,
  useGetNivClinicTagsRegistryQuery,
  useNivPrintListPdfMutation,
  useUpdateClinicFieldMutation,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import {
  buildStandingOrderQueue,
  getNextPayment,
  getPayingSummary,
  isNoUsage,
  SEGMENTS,
  sumClinicRevenue,
} from "../shared/nivDerived";
import ClinicsOverviewHeader from "./ClinicsOverviewHeader";
import ClinicsSegmentTabs from "./ClinicsSegmentTabs";
import ClinicsToolbar, { PLAN_OPTIONS, SORT_OPTIONS } from "./ClinicsToolbar";
import { buildClinicsPrintPayload } from "../shared/printModels";
import { openPdfInNewTab } from "../shared/openPdf";
import ClinicsList from "./ClinicsList";
import TagManagerModal from "./TagManagerModal";
import "./NivClinicsTab.css";

const SEGMENT_STORAGE_KEY = "clinikit_nivclinics_segment";
const SEGMENT_IDS = new Set(SEGMENTS.map((s) => s.id));

/** Normalize any typed phone (05…, +972…, dashes/spaces) to local digits for search. */
const phoneToLocalDigits = (raw) => {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.startsWith("972")) digits = "0" + digits.slice(3);
  return digits;
};

const readStoredSegment = () => {
  try {
    const v = localStorage.getItem(SEGMENT_STORAGE_KEY);
    return v && SEGMENT_IDS.has(v) ? v : "all";
  } catch {
    return "all";
  }
};

function NivClinicsTab({ isAuthorized }) {
  const navigate = useNavigate();
  const { showError } = useToast();
  const therapistName = useSelector(selectTherapistName);
  const author = therapistName || "NiV";

  const [segment, setSegment] = useState(readStoredSegment);
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [noUsageOnly, setNoUsageOnly] = useState(false);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [reactivateRow, setReactivateRow] = useState(null);

  // ---- Data ----
  const { data: allRows = [], isLoading } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: usageRows = [] } = useGetClinicUsageQuery(undefined, { skip: !isAuthorized });
  const { data: registry, isError: tagsError } = useGetNivClinicTagsRegistryQuery(undefined, {
    skip: !isAuthorized,
  });
  useEffect(() => {
    if (tagsError) showError("לא ניתן לטעון את התגיות — יש לפרוס את חוקי Firestore");
  }, [tagsError, showError]);

  const tags = useMemo(() => registry?.tags || [], [registry]);
  const tagMap = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);
  const usageMap = useMemo(
    () => Object.fromEntries((usageRows || []).map((u) => [u.id || u.clinicId, u])),
    [usageRows]
  );
  const usageByTag = useMemo(() => {
    const counts = {};
    allRows.forEach((r) => (r.crm?.tagIds || []).forEach((id) => (counts[id] = (counts[id] || 0) + 1)));
    return counts;
  }, [allRows]);
  // Unverified standing-order months per clinic — turns the "next payment"
  // cell red until the admin confirms them in the morning briefing
  const pendingSoByClinic = useMemo(() => {
    const counts = {};
    buildStandingOrderQueue(allRows).forEach((entry) => {
      counts[entry.row.clinicId] = (counts[entry.row.clinicId] || 0) + 1;
    });
    return counts;
  }, [allRows]);

  const [updateClinicField] = useUpdateClinicFieldMutation();
  const [printList, { isLoading: isPrinting }] = useNivPrintListPdfMutation();

  // ---- Derived lists ----
  const segmentCounts = useMemo(() => {
    const counts = {};
    SEGMENTS.forEach((s) => (counts[s.id] = allRows.filter(s.predicate).length));
    return counts;
  }, [allRows]);

  // Header stats always describe the active (non-deactivated) population
  const activeRows = useMemo(() => allRows.filter((r) => !r.inactiveByAdmin), [allRows]);

  const segmentRows = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.id === segment) || SEGMENTS[0];
    return allRows.filter(seg.predicate);
  }, [allRows, segment]);

  const filteredRows = useMemo(() => {
    let list = [...segmentRows];
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const termDigits = phoneToLocalDigits(term);
      list = list.filter((r) => {
        const nameMatch =
          (r.name || "").toLowerCase().includes(term) ||
          (r.ownerName || "").toLowerCase().includes(term);
        const phoneMatch =
          termDigits.length >= 3 && phoneToLocalDigits(r.ownerPhone).includes(termDigits);
        return nameMatch || phoneMatch;
      });
    }
    if (planFilter !== "all") list = list.filter((r) => r.plan === planFilter);
    if (selectedTagIds.length > 0) {
      list = list.filter((r) => selectedTagIds.every((id) => (r.crm?.tagIds || []).includes(id)));
    }
    if (noUsageOnly) list = list.filter((r) => isNoUsage(usageMap[r.clinicId]));

    const dir = sortDir === "asc" ? 1 : -1;
    const time = (v) => (v ? new Date(v).getTime() : 0);
    // "No value" always sinks to the bottom regardless of direction
    const NONE = Number.MAX_SAFE_INTEGER;
    list.sort((a, b) => {
      switch (sortField) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "", "he");
        case "patients":
          return dir * ((a.totalPatients || 0) - (b.totalPatients || 0));
        case "amount":
          return (
            dir *
            ((getPayingSummary(a.crm)?.amountPreVat || 0) - (getPayingSummary(b.crm)?.amountPreVat || 0))
          );
        case "revenue":
          return dir * (sumClinicRevenue(a.crm).preVat - sumClinicRevenue(b.crm).preVat);
        case "nextPayment": {
          const next = (r) => {
            const n = getNextPayment(r, pendingSoByClinic[r.clinicId] || 0);
            return n ? time(n.date) : null;
          };
          const na = next(a);
          const nb = next(b);
          if (na === null && nb === null) return 0;
          if (na === null) return 1;
          if (nb === null) return -1;
          return dir * (na - nb);
        }
        // Never logged in sorts as "longest ago", not as 0
        case "lastLoginAt": {
          const since = (id) => {
            const d = usageMap[id]?.logins?.daysSinceLastLogin;
            return d == null ? NONE : Number(d);
          };
          return dir * (since(b.clinicId) - since(a.clinicId));
        }
        case "lastContactAt":
          return dir * (time(a.crm?.lastContactAt) - time(b.crm?.lastContactAt));
        case "createdAt":
        default:
          return dir * (time(a.createdAt) - time(b.createdAt));
      }
    });
    return list;
  }, [segmentRows, searchTerm, planFilter, selectedTagIds, noUsageOnly, sortField, sortDir, usageMap, pendingSoByClinic]);

  const isFiltered = !!searchTerm.trim() || planFilter !== "all" || selectedTagIds.length > 0 || noUsageOnly;

  // ---- Handlers ----
  const handleSegment = (id) => {
    setSegment(id);
    try {
      localStorage.setItem(SEGMENT_STORAGE_KEY, id); // explicit click only
    } catch {
      /* ignore */
    }
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      // Names A→Z and "next payment" nearest-first read ascending; everything else biggest/latest first
      setSortDir(field === "name" || field === "nextPayment" ? "asc" : "desc");
    }
  };

  const toggleTag = (id) =>
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Re-enable a clinic that was deactivated by admin (inactiveByAdmin)
  const handleReactivate = async () => {
    const row = reactivateRow;
    if (!isAuthorized || !row) return;
    try {
      await updateClinicField({
        clinicId: row.clinicId,
        fieldUpdates: { inactiveByAdmin: false },
        skipTimestamp: true, // don't bump updatedAt (activity proxy)
      }).unwrap();
      setReactivateRow(null);
    } catch (error) {
      console.error("[CK-7302] Error reactivating clinic:", {
        errorCode: "CK-7302",
        errorMessage: error?.message,
        clinicId: row.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהפעלת הקליניקה מחדש");
    }
  };

  const handlePrint = async () => {
    // Open the tab synchronously (popup blockers), fill it once the PDF is ready
    const tab = openPdfInNewTab();
    try {
      const payload = buildClinicsPrintPayload(filteredRows, usageMap, pendingSoByClinic, {
        segmentLabel: SEGMENTS.find((s) => s.id === segment)?.label,
        planLabel: planFilter !== "all" ? PLAN_OPTIONS.find((o) => o.value === planFilter)?.label : null,
        tagLabels: selectedTagIds.map((id) => tags.find((t) => t.id === id)?.label).filter(Boolean),
        noUsageOnly,
        searchTerm: searchTerm.trim(),
        sortLabel: `${SORT_OPTIONS.find((o) => o.value === sortField)?.label || ""} (${sortDir === "asc" ? "עולה" : "יורד"})`,
      });
      const result = await printList(payload).unwrap();
      tab.show(result.pdfUrl);
    } catch (error) {
      tab.close();
      console.error("[CK-9823] Error printing clinics list:", {
        errorCode: "CK-9823",
        errorMessage: error?.message,
        rowCount: filteredRows.length,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהכנת קובץ ההדפסה");
    }
  };

  const handleOpen = useCallback((row) => navigate(`/dashboard/niv/clinic/${row.clinicId}`), [navigate]);
  const openManager = useCallback(() => setTagManagerOpen(true), []);

  if (isLoading) {
    return (
      <ContentLoader message="טוען קליניקות..." />
    );
  }

  return (
    <div className="nivck2-page nivut-scope">
      <ClinicsOverviewHeader
        rows={activeRows}
        inactiveCount={allRows.length - activeRows.length}
      />

      <div className="nivck2-board">
        <ClinicsSegmentTabs active={segment} counts={segmentCounts} onChange={handleSegment} />

        <div className="nivck2-board-body">
          <ClinicsToolbar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            tags={tags}
            selectedTagIds={selectedTagIds}
            onToggleTag={toggleTag}
            onClearTags={() => {
              setSelectedTagIds([]);
              setNoUsageOnly(false);
            }}
            noUsageOnly={noUsageOnly}
            onToggleNoUsage={() => setNoUsageOnly((v) => !v)}
            planFilter={planFilter}
            onPlanFilter={setPlanFilter}
            sortField={sortField}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            resultCount={filteredRows.length}
            onManageTags={openManager}
            onPrint={handlePrint}
            isPrinting={isPrinting}
          />

          <ClinicsList
            rows={filteredRows}
            segment={segment}
            filtered={isFiltered}
            usageMap={usageMap}
            pendingSoByClinic={pendingSoByClinic}
            tagMap={tagMap}
            tags={tags}
            author={author}
            onOpen={handleOpen}
            onManageTags={openManager}
            onReactivate={setReactivateRow}
          />
        </div>
      </div>

      {tagManagerOpen && (
        <TagManagerModal tags={tags} usageByTag={usageByTag} onClose={() => setTagManagerOpen(false)} />
      )}

      <ConfirmDialog
        isOpen={!!reactivateRow}
        onClose={() => setReactivateRow(null)}
        onConfirm={handleReactivate}
        title="הפעלה מחדש"
        message={reactivateRow ? `להפעיל מחדש את הקליניקה "${reactivateRow.name || reactivateRow.clinicId}"?` : ""}
        confirmText="הפעל מחדש"
        type="info"
      />
    </div>
  );
}

export default NivClinicsTab;
